import {
  decodeJwt,
  isTokenExpired,
  companyIdOf,
  isPlatformToken,
  extractVisualKeys,
  SUPER_ADMIN_ROLE,
} from '../auth/jwt';
import { clearSession } from '../auth/session-storage';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrentUserResponse } from '../../interfaces/usersmanagement-interface';
import { SKIP_AUTH } from '../interceptors/skip-auth.interceptor-context';
import { SaveDayChangesDTO } from './../../pages/manager-calander-view/schedule-reponse-interface';
import { SKIP_LOADER } from './skip-loader.token';
import { APIResponse } from '../../interfaces/apiresponse-interface';
import {
  SchedulePublishPreview,
  SchedulePublishRequest,
  SchedulePublishState,
} from '../../interfaces/schedule-publish-interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
  baseUrl = environment.api.serverUrl;

  private isAuthenticated$ = new BehaviorSubject<boolean>(false);

  tokenKey = 'auth_token';
  userKey = 'user_data';

  constructor(private http: HttpClient) {}

  headers = new HttpHeaders({
    'Content-Type': 'application/json',
  });
  header = this.headers;

  getToken(): string | null {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(this.tokenKey);
    }
    return null;
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  getCurrentUserRole(): string[] {
    const token = this.getToken();
    if (!token) return [];
    const userData: CurrentUserResponse = JSON.parse(localStorage.getItem(this.userKey) || '{}');
    if (!userData) return [];

    return userData?.roles || [];
  }

  getCurrentUserId(): string | null {
    const token = this.getToken();
    if (!token) return null;
    const userData: CurrentUserResponse = JSON.parse(localStorage.getItem(this.userKey) || '{}');
    if (!userData) return null;
    return userData?.id || null;
  }

  /**
   * Check if the current user has access to one or more features
   * @param features - Single feature key (string) or array of feature keys
   * @returns true if user has at least one of the specified features, false otherwise
   */

  hasFeatureAccess(features: string | string[]): boolean {
    const token = this.getToken();
    if (!token) return false;

    const userData: CurrentUserResponse = JSON.parse(localStorage.getItem(this.userKey) || '{}');
    if (!userData) return false;

    // Admin bypasses all feature gates
    if (userData.roles?.includes('Admin')) return true;

    if (!userData.accessFeatures) return false;

    const requiredFeatures = Array.isArray(features) ? features : [features];
    if (requiredFeatures.length === 0) return true;

    const userFeatureKeys = this.extractVisualKeys(userData.accessFeatures);
    return requiredFeatures.some((key) => userFeatureKeys.includes(key));
  }

  /**
   * Extract all visualKey values from nested accessFeatures structure
   * @param features - Array of feature objects with potential nested children
   * @returns Array of all visualKey strings
   */

  private extractVisualKeys(features: any[]): string[] {
    return extractVisualKeys(features);
  }

  isTokenExpired(token: string): boolean {
    return isTokenExpired(token);
  }

  /**
   * Kept for callers that already use it. The implementation now handles base64url, which the
   * previous atob-only version did not — it threw on any token whose payload contained '-' or '_'.
   */
  public decodeToken(token: string): any {
    return decodeJwt(token);
  }

  /** Company this token belongs to, or null for a token that carries no company claim. */
  getCurrentCompanyId(): string | null {
    return companyIdOf(this.getToken());
  }

  /** True for a SuperAdmin: platform scope rather than a company one. */
  isPlatformUser(): boolean {
    return isPlatformToken(this.getToken());
  }

  isSuperAdmin(): boolean {
    return this.isPlatformUser() || this.getCurrentUserRole().includes(SUPER_ADMIN_ROLE);
  }

  getAuthStatus() {
    return this.isAuthenticated$;
  }

  login(credentials: { identifier: string; password: string }): Observable<any> {
    const payload = {
      ...credentials,
    };

    return this.http.post<any>(`${this.baseUrl}/usermanagement/Auth/login`, payload, {
      context: new HttpContext().set(SKIP_AUTH, true),
      headers: this.header,
    });
  }

  getCurrentUser(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/usermanagement/User/current-user`, {
      headers: this.header,
    });
  }

  sendConfirmationEmail(data: { userId: string }): Observable<any> {
    const payload = {
      ...data,
    };
    return this.http.post<any>(
      `${this.baseUrl}/usermanagement/User/send-confirmation-email`,
      payload,
      {
        headers: this.header,
      },
    );
  }

  updateUserStatus(data: { userId: string; isActive: boolean }): Observable<any> {
    const payload = {
      ...data,
    };
    return this.http.post<any>(`${this.baseUrl}/usermanagement/User/update-user-status`, payload, {
      headers: this.header,
    });
  }

  setFirstPassword(data: {
    userId: string;
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<any> {
    const payload = {
      ...data,
    };
    return this.http.post<any>(
      `${this.baseUrl}/usermanagement/Auth/set-initial-password`,
      payload,
      {
        headers: this.header,
      },
    );
  }

  requestResetPassword(data: { email: string }): Observable<any> {
    const payload = {
      ...data,
    };
    return this.http.post<any>(`${this.baseUrl}/usermanagement/Auth/request-reset-otp`, payload, {
      context: new HttpContext().set(SKIP_AUTH, true),
      headers: this.header,
    });
  }

  verifyResetOTP(data: { email: string; otp: string }): Observable<any> {
    const payload = {
      ...data,
    };
    return this.http.post<any>(`${this.baseUrl}/usermanagement/Auth/verify-reset-otp`, payload, {
      context: new HttpContext().set(SKIP_AUTH, true),
      headers: this.header,
    });
  }

  resetPasswordWithOTP(data: { email: string; otp: string; newPassword: string }): Observable<any> {
    const payload = {
      ...data,
    };
    return this.http.post<any>(
      `${this.baseUrl}/usermanagement/Auth/reset-password-with-otp`,
      payload,
      {
        context: new HttpContext().set(SKIP_AUTH, true),
        headers: this.header,
      },
    );
  }

  getUsers(options?: {
    filters?: any;
    pagination?: { page: number; pageSize: number };
    sort?: { field: string; direction: 'asc' | 'desc' };
  }): Observable<any> {
    const payload: any = {};
    if (options?.filters) {
      payload.filters = options.filters;
    }
    if (options?.pagination) {
      payload.pagination = options.pagination;
    }
    if (options?.sort) {
      payload.sort = options.sort;
    }
    return this.http.post<any>(`${this.baseUrl}/usermanagement/User/getusers`, payload, {
      headers: this.header,
    });
  }

  getCreateUserLookups(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/usermanagement/User/lookup`, {
      headers: this.header,
    });
  }

  getActivityLogs(options?: {
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
    userName?: string;
    action?: string;
    entity?: string;
    fromDate?: string;
    toDate?: string;
    archivedOnly?: boolean;
  }): Observable<any> {
    //CREATE query params from options
    let params = new HttpParams();
    if (options?.page) {
      params = params.set('page', options.page.toString());
    }
    if (options?.pageSize) {
      params = params.set('pageSize', options.pageSize.toString());
    }
    if (options?.sortField) {
      params = params.set('sortField', options.sortField);
    }
    if (options?.sortDirection) {
      params = params.set('sortDirection', options.sortDirection);
    }
    if (options?.userName) {
      params = params.set('userName', options.userName);
    }
    if (options?.action) {
      params = params.set('action', options.action);
    }
    if (options?.entity) {
      params = params.set('entity', options.entity);
    }
    if (options?.fromDate) {
      params = params.set('fromDate', options.fromDate);
    }
    if (options?.toDate) {
      params = params.set('toDate', options.toDate);
    }
    if (options?.archivedOnly) {
      params = params.set('archivedOnly', 'true');
    }
    return this.http.get<any>(`${this.baseUrl}/usermanagement/ActivityLog`, {
      headers: this.header,
      params: params,
    });
  }

  /**
   * Fetches ALL users by making multiple paginated requests if needed.
   * This is useful for simulations or dropdowns that need all users.
   * @param options Optional filters and sorting parameters (excludes pagination params)
   * @returns Observable with all users combined
   */
  getAllUsers(options?: {
    filters?: any;
    sort?: { field: string; direction: 'asc' | 'desc' };
  }): Observable<any> {
    // First fetch to get total count
    return this.getUsers({ ...options, pagination: { page: 1, pageSize: 10 } }).pipe(
      switchMap((firstResponse) => {
        const totalRecords = firstResponse.data?.totalRecords || 0;
        const pageSize = 10;
        const totalPages = Math.ceil(totalRecords / pageSize);

        // If only one page, return directly
        if (totalPages <= 1) {
          return new Observable((observer) => {
            observer.next(firstResponse);
            observer.complete();
          });
        }

        // Create array of remaining page requests
        const pageRequests = [];
        for (let page = 2; page <= totalPages; page++) {
          pageRequests.push(this.getUsers({ ...options, pagination: { page, pageSize } }));
        }

        // Execute all requests in parallel and combine results
        return forkJoin(pageRequests).pipe(
          map((responses) => {
            const allUsers = [...firstResponse.data.users];
            responses.forEach((response) => {
              allUsers.push(...response.data.users);
            });

            return {
              ...firstResponse,
              data: {
                ...firstResponse.data,
                users: allUsers,
                totalRecords: allUsers.length,
              },
            };
          }),
        );
      }),
    );
  }

  getUserById(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/usermanagement/User/${id}`, {
      headers: this.header,
    });
  }

  getManagerUsers(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/usermanagement/User/managers`, {
      headers: this.header,
    });
  }

  updateUser(id: string, userData: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/usermanagement/User/${id}`, userData, {
      headers: this.header,
    });
  }

  deleteUsers(ids: string[]): Observable<any> {
    return this.http.request<any>('delete', `${this.baseUrl}/usermanagement/User`, {
      headers: this.header,
      body: [...ids],
    });
  }

  /**
   * Schedule API: List all schedules (with filters, sorting, and pagination)
   */
  getSchedules(options?: {
    filters?: any;
    pagination?: { page: number; pageSize: number };
    sort?: { field: string; direction: 'asc' | 'desc' };
  }): Observable<any> {
    const payload = {
      filters: options?.filters || {},
      pagination: options?.pagination || { page: 1, pageSize: 10 },
      sort: options?.sort || { field: 'generationTime', direction: 'desc' },
    };
    return this.http.post<any>(`${this.baseUrl}/Schedule/getschedules`, payload, {
      headers: this.header,
    });
  }

  /**
   * Schedule API: Get schedule by id
   */
  getScheduleById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Schedule/${id}`, {
      headers: this.header,
    });
  }

  /**
   * Schedule API: Get shift's hourly breakdown by shift id
   */
  getShiftHourlyBreakdown(shiftId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Schedule/shifts/${shiftId}/hourly-breakdown`, {
      headers: this.header,
    });
  }

  /**
   * Save hourly breakdown changes for a shift
   */
  saveShiftHourlyBreakdown(
    scheduleId: number,
    agentId: string,
    date: string,
    teamId: string,
    changes: SaveDayChangesDTO,
  ): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/Schedule/${scheduleId}/agents/${agentId}/days/${date}?teamId=${teamId}`,
      changes,
      {
        headers: this.header,
      },
    );
  }

  /**
   * Schedule API: Generate simulated schedule (creates Schedule + Shifts + Breaks + Assignments)
   */
  generateSimulatedSchedule(payload: {
    startDate: string;
    endDate: string;
    notes?: string;
    complianceRules?: {
      region: string;
      jurisdiction: string;
      rules: string[];
    };
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Schedule/generate-simulated`, payload, {
      headers: this.header,
    });
  }

  /**
   * Schedule API: Publish a schedule
   */
  getAgentsForManualSchedule(teamIds: number[]): Observable<any> {
    const qs = teamIds.map(id => `teamIds=${id}`).join('&');
    return this.http.get<any>(`${this.baseUrl}/Schedule/manual/agents?${qs}`, { headers: this.header });
  }

  createManualSchedule(payload: {
    teamIds: number[];
    agentIds?: string[];
    startDate: string;
    endDate: string;
    notes?: string;
    restBreak?: { enabled: boolean; durationMinutes: number; offsetFromShiftStartMinutes: number };
    mealBreak?: { enabled: boolean; durationMinutes: number; offsetFromShiftStartMinutes: number };
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Schedule/manual`, payload, { headers: this.header });
  }

  publishSchedule(id: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Schedule/${id}/publish`, null, {
      headers: this.header,
    });
  }

  /** Scheduled publish: the pending publish (if any) and the zone the dialog starts on. */
  getScheduledPublish(id: number): Observable<APIResponse<SchedulePublishState>> {
    return this.http.get<APIResponse<SchedulePublishState>>(
      `${this.baseUrl}/Schedule/${id}/schedule-publish`,
      { headers: this.header },
    );
  }

  /** Scheduled publish: validation and consequences for a time, without changing anything. */
  previewScheduledPublish(
    id: number,
    body: SchedulePublishRequest,
  ): Observable<APIResponse<SchedulePublishPreview>> {
    return this.http.post<APIResponse<SchedulePublishPreview>>(
      `${this.baseUrl}/Schedule/${id}/schedule-publish/preview`,
      body,
      { headers: this.header },
    );
  }

  /** Scheduled publish: schedule, or reschedule an existing one. */
  schedulePublish(
    id: number,
    body: SchedulePublishRequest,
  ): Observable<APIResponse<SchedulePublishPreview>> {
    return this.http.post<APIResponse<SchedulePublishPreview>>(
      `${this.baseUrl}/Schedule/${id}/schedule-publish`,
      body,
      { headers: this.header },
    );
  }

  cancelScheduledPublish(id: number): Observable<APIResponse<null>> {
    return this.http.delete<APIResponse<null>>(`${this.baseUrl}/Schedule/${id}/schedule-publish`, {
      headers: this.header,
    });
  }

  deactivateSchedule(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/Schedule/${id}/deactivate`, null, {
      headers: this.header,
    });
  }

  /**
   * Schedule API: Delete a schedule
   */
  deleteSchedule(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/Schedule/${id}`, {
      headers: this.header,
    });
  }

  /**
   * Schedule API: Publish draft schedule and create shifts
   */
  publishScheduleWithShifts(id: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Schedule/${id}/publish-with-shifts`, null, {
      headers: this.header,
    });
  }

  /**
   * Schedule API: Update published schedule
   */
  updatePublishedSchedule(scheduleId: number, updatedData: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/Schedule/${scheduleId}/update`, updatedData, {
      headers: this.header,
    });
  }

  /**
   * Schedule API: Get forecast data for a schedule (Phase 3)
   */
  getScheduleForecast(scheduleId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Schedule/${scheduleId}/forecast`, {
      headers: this.header,
    });
  }

  /**
   * Shift APIs
   */
  getMyShifts(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Shift/my-shifts`, { headers: this.header });
  }

  getShiftById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Shift/${id}`, { headers: this.header });
  }

  getTeamShifts(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Shift/team-shifts`, { headers: this.header });
  }

  /**
   * Break APIs
   */
  // takeBreak(breakId: number): Observable<any> {
  //   return this.http.post<any>(`${this.baseUrl}/Break/${breakId}/take`, null, { headers: this.header });
  // }

  // endBreak(breakId: number): Observable<any> {
  //   return this.http.post<any>(`${this.baseUrl}/Break/${breakId}/end`, null, { headers: this.header });
  // }

  // skipBreak(breakId: number, reason: string): Observable<any> {
  //   return this.http.post<any>(`${this.baseUrl}/Break/${breakId}/skip`, { reason }, { headers: this.header });
  // }

  // snoozeBreak(breakId: number): Observable<any> {
  //   return this.http.post<any>(`${this.baseUrl}/Break/${breakId}/snooze`, null, { headers: this.header });
  // }

  /**
   * Violation API
   */
  // resolveViolation(violationId: number, resolutionNotes: string): Observable<any> {
  //   return this.http.post<any>(`${this.baseUrl}/Violation/${violationId}/resolve`, { resolutionNotes }, { headers: this.header });
  // }

  /**
   * ScheduleAssignment APIs
   */
  getAssignmentsBySchedule(
    scheduleId: number,
    pageNumber: number = 1,
    pageSize: number = 10,
    search: string = '',
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<any>(`${this.baseUrl}/ScheduleAssignment/by-schedule/${scheduleId}`, {
      headers: this.header,
      params,
    });
  }

  getAssignmentsByUser(userId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ScheduleAssignment/by-user/${userId}`, {
      headers: this.header,
    });
  }

  getMySchedule(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ScheduleAssignment/my-schedule`, {
      headers: this.header,
    });
  }

  applyUserSchedule(payload: {
    scheduleId: number;
    userId: string;
    weekdays: string[];
    shiftStartTime: string; // ISO datetime string
    shiftEndTime: string;
    mealBreaks: { startTime: string; endTime: string }[];
    restBreaks: { startTime: string; endTime: string }[];
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ScheduleAssignment/apply-user-schedule`, payload, {
      headers: this.header,
    });
  }

  getRoles(options?: {
    sortKey?: string;
    sortOrder?: 'asc' | 'desc';
    Search?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Observable<any> {
    let params = '';
    if (options?.sortKey) {
      params += `?SortBy=${options.sortKey}`;
      if (options.sortOrder) {
        params += `&Ascending=${options.sortOrder === 'asc' ? 'true' : 'false'}`;
      }
    }
    if (options?.Search) {
      params += params ? `&Search=${options.Search}` : `?Search=${options.Search}`;
    }
    if (options?.pageNumber !== undefined) {
      params += params ? `&PageNumber=${options.pageNumber}` : `?PageNumber=${options.pageNumber}`;
    }
    if (options?.pageSize !== undefined) {
      params += params ? `&PageSize=${options.pageSize}` : `?PageSize=${options.pageSize}`;
    }
    return this.http.get<any>(`${this.baseUrl}/usermanagement/roles${params}`, {
      headers: this.header,
    });
  }

  duplicateRole(id: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/usermanagement/roles/roles/copy?sourceRoleId=${id}`,
      null,
      {
        headers: this.header,
      },
    );
  }

  getRoleById(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/usermanagement/roles/${id}`, {
      headers: this.header,
    });
  }

  createRole(roleData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usermanagement/roles`, roleData, {
      headers: this.header,
    });
  }

  editRole(id: string, roleData: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/usermanagement/roles/${id}`, roleData, {
      headers: this.header,
    });
  }

  deleteRoles(ids: number[]): Observable<any> {
    return this.http.request<any>('delete', `${this.baseUrl}/usermanagement/roles`, {
      headers: this.header,
      body: [...ids],
    });
  }

  createUser(userData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usermanagement/User`, userData, {
      headers: this.header,
    });
  }

  getFeatures(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/usermanagement/roles/features`, {
      headers: this.header,
    });
  }

  /**
   * Break Workflow APIs
   */
  takeBreak(breakId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Break/${breakId}/take`, null, {
      headers: this.header,
    });
  }

  endBreak(breakId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Break/${breakId}/end`, null, {
      headers: this.header,
    });
  }

  snoozeBreak(breakId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Break/${breakId}/snooze`, null, {
      headers: this.header,
    });
  }

  skipBreak(breakId: number, reason: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/Break/${breakId}/skip`,
      { reason },
      { headers: this.header },
    );
  }

  /**
   * Violation APIs
   */
  getMyViolations(from?: string, to?: string, archivedOnly = false): Observable<any> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (to)   params = params.set('to',   to);
    if (archivedOnly) params = params.set('archivedOnly', 'true');
    return this.http.get<any>(`${this.baseUrl}/Violation/my-violations`, {
      headers: this.header,
      params,
    });
  }

  getTeamViolations(teamId?: number, from?: string, to?: string, archivedOnly = false): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined) params = params.set('teamId', teamId.toString());
    if (from) params = params.set('from', from);
    if (to)   params = params.set('to',   to);
    if (archivedOnly) params = params.set('archivedOnly', 'true');
    return this.http.get<any>(`${this.baseUrl}/Violation/team-violations`, {
      headers: this.header,
      params,
    });
  }

  getAllViolations(teamId?: number, from?: string, to?: string, archivedOnly = false): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined) params = params.set('teamId', teamId.toString());
    if (from) params = params.set('from', from);
    if (to)   params = params.set('to',   to);
    if (archivedOnly) params = params.set('archivedOnly', 'true');
    return this.http.get<any>(`${this.baseUrl}/Violation`, {
      headers: this.header,
      params,
    });
  }

  // ── Archive / unarchive (bulk) ──────────────────────────────────────────
  archiveUsers(ids: string[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usermanagement/User/archive`, ids, { headers: this.header });
  }
  unarchiveUsers(ids: string[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usermanagement/User/unarchive`, ids, { headers: this.header });
  }
  archiveSchedules(ids: number[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Schedule/archive`, ids, { headers: this.header });
  }
  unarchiveSchedules(ids: number[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Schedule/unarchive`, ids, { headers: this.header });
  }
  archiveViolations(ids: number[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Violation/archive`, ids, { headers: this.header });
  }
  unarchiveViolations(ids: number[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Violation/unarchive`, ids, { headers: this.header });
  }
  archiveActivityLogs(ids: number[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usermanagement/ActivityLog/archive`, ids, { headers: this.header });
  }
  unarchiveActivityLogs(ids: number[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usermanagement/ActivityLog/unarchive`, ids, { headers: this.header });
  }

  // Heatmap takes its own date range (default = This Week, computed server-side in team TZ).
  getStaffingHeatmap(teamId?: number, startDate?: string, endDate?: string): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined)    params = params.set('teamId', teamId.toString());
    if (startDate)               params = params.set('startDate', startDate);
    if (endDate)                 params = params.set('endDate', endDate);
    return this.http.get<any>(`${this.baseUrl}/Dashboard/staffing-heatmap`, {
      headers: this.header,
      params,
    });
  }

  // KPI endpoints share an `at` ISO timestamp = wall-clock hour in team TZ (default current hour).
  getForecastAccuracy(teamId?: number, at?: string): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined) params = params.set('teamId', teamId.toString());
    if (at)                   params = params.set('at', at);
    return this.http.get<any>(`${this.baseUrl}/Dashboard/forecast-accuracy`, {
      headers: this.header,
      params,
    });
  }

  getAverageAdherence(teamId?: number, at?: string): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined) params = params.set('teamId', teamId.toString());
    if (at)                   params = params.set('at', at);
    return this.http.get<any>(`${this.baseUrl}/Dashboard/average-adherence`, {
      headers: this.header,
      params,
    });
  }

  getIntraDayStats(teamId?: number, at?: string): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined) params = params.set('teamId', teamId.toString());
    if (at)                   params = params.set('at', at);
    return this.http.get<any>(`${this.baseUrl}/Dashboard/intraday-management-stats`, {
      headers: this.header,
      params,
    });
  }

  getSchedulingEfficiency(teamId?: number, at?: string): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined) params = params.set('teamId', teamId.toString());
    if (at)                   params = params.set('at', at);
    return this.http.get<any>(`${this.baseUrl}/Dashboard/scheduling-efficiency`, {
      headers: this.header,
      params,
    });
  }

  getSkillBreakdown(teamId?: number, at?: string): Observable<any> {
    let params = new HttpParams();
    if (teamId !== undefined) params = params.set('teamId', teamId.toString());
    if (at)                   params = params.set('at', at);
    return this.http.get<any>(`${this.baseUrl}/Dashboard/skill-breakdown`, {
      headers: this.header,
      params,
    });
  }

  getCallVolumeClientIds(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Dashboard/call-volume-client-ids`, {
      headers: this.header,
    });
  }

  getCallVolumeBySkills(startDate: string, endDate: string, cltIds?: number[]): Observable<any> {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    if (cltIds && cltIds.length > 0) {
      cltIds.forEach(id => { params = params.append('cltIds', id.toString()); });
    }
    return this.http.get<any>(`${this.baseUrl}/Dashboard/call-volume-by-skills`, {
      headers: this.header,
      params,
    });
  }

  getActualVsClocked(startDate: string, endDate: string, teamId?: number): Observable<any> {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    if (teamId != null) params = params.set('teamId', teamId.toString());
    return this.http.get<any>(`${this.baseUrl}/Dashboard/actual-vs-clocked`, {
      headers: this.header,
      params,
    });
  }

  createViolation(payload: {
    userId: number;
    shiftId: number;
    breakId?: number;
    violationType: string;
    description: string;
    status: string;
    timestamp: string;
    premiumPayRequired?: boolean;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Violation`, payload, {
      headers: this.header,
    });
  }

  resolveViolation(violationId: number, payload: { resolutionNotes: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Violation/${violationId}/resolve`, payload, {
      headers: this.header,
    });
  }

  // Shift actions
  startShift(shiftId: number): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/Shift/${shiftId}/start`,
      {},
      {
        headers: this.header,
      },
    );
  }

  endShift(shiftId: number): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/Shift/${shiftId}/end`,
      {},
      {
        headers: this.header,
      },
    );
  }

  /**
   * GET /api/Shift is paginated and PagingParametersDto defaults PageSize to 10, so callers
   * that need the full set (reports) must ask for a large page explicitly — otherwise only
   * the 10 highest-Id shifts come back.
   */
  getAllShifts(pageSize = 10000, pageNumber = 1): Observable<any> {
    const params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));
    return this.http.get<any>(`${this.baseUrl}/Shift`, {
      headers: this.header,
      params,
    });
  }

  /**
   * Adherence API methods
   */

  // Get adherence metrics with filters
  getAdherenceMetrics(options?: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    teamIds?: number[];
  }): Observable<any> {
    let params = new HttpParams();
    if (options?.startDate) {
      params = params.set('startDate', options.startDate);
    }
    if (options?.endDate) {
      params = params.set('endDate', options.endDate);
    }
    if (options?.userId) {
      params = params.set('userId', options.userId);
    }
    if (options?.teamIds && options.teamIds.length > 0) {
      options.teamIds.forEach((id) => {
        params = params.append('teamIds', id.toString());
      });
    }
    return this.http.get<any>(`${this.baseUrl}/Adherence/metrics`, {
      headers: this.header,
      params,
    });
  }

  // Get adherence records with filters
  getAdherenceRecords(options?: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    teamIds?: number[];
    statusFilters?: number[];
  }): Observable<any> {
    let params = new HttpParams();
    if (options?.startDate) {
      params = params.set('startDate', options.startDate);
    }
    if (options?.endDate) {
      params = params.set('endDate', options.endDate);
    }
    if (options?.userId) {
      params = params.set('userId', options.userId);
    }
    if (options?.teamIds && options.teamIds.length > 0) {
      options.teamIds.forEach((id) => {
        params = params.append('teamIds', id.toString());
      });
    }
    if (options?.statusFilters && options.statusFilters.length > 0) {
      options.statusFilters.forEach((status) => {
        params = params.append('statusFilters', status.toString());
      });
    }
    return this.http.get<any>(`${this.baseUrl}/Adherence`, {
      headers: this.header,
      params,
    });
  }

  // Calculate adherence manually
  calculateAdherence(payload: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    forceRecalculate?: boolean;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/Adherence/calculate`, payload, {
      headers: this.header,
    });
  }

  // Get adherence trend data
  getAdherenceTrend(options?: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    teamIds?: number[];
  }): Observable<any> {
    let params = new HttpParams();
    if (options?.startDate) {
      params = params.set('startDate', options.startDate);
    }
    if (options?.endDate) {
      params = params.set('endDate', options.endDate);
    }
    if (options?.userId) {
      params = params.set('userId', options.userId);
    }
    if (options?.teamIds && options.teamIds.length > 0) {
      options.teamIds.forEach((id) => {
        params = params.append('teamIds', id.toString());
      });
    }
    return this.http.get<any>(`${this.baseUrl}/Adherence/trend`, {
      headers: this.header,
      params,
    });
  }

  /**
   * ComplianceRuleset API methods
   */

  // Get all compliance rulesets
  getComplianceRulesets(filters?: { regionId?: number; isActive?: boolean }): Observable<any> {
    let params = new HttpParams();
    if (filters?.regionId) {
      params = params.set('regionId', filters.regionId.toString());
    }
    if (filters?.isActive !== undefined) {
      params = params.set('isActive', filters.isActive.toString());
    }
    return this.http.get<any>(`${this.baseUrl}/ComplianceRuleset`, {
      headers: this.header,
      params,
    });
  }

  // Get active compliance ruleset
  getActiveComplianceRuleset(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ComplianceRuleset/active`, {
      headers: this.header,
    });
  }

  // Get compliance ruleset by ID
  getComplianceRulesetById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ComplianceRuleset/${id}`, {
      headers: this.header,
    });
  }

  // Create compliance ruleset
  createComplianceRuleset(payload: {
    regionId: number;
    rules: string[];
    isActive: boolean;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ComplianceRuleset`, payload, {
      headers: this.header,
    });
  }

  // Update compliance ruleset
  updateComplianceRuleset(
    id: number,
    payload: {
      regionId: number;
      rules: string[];
      isActive: boolean;
    },
  ): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/ComplianceRuleset/${id}`, payload, {
      headers: this.header,
    });
  }

  // Delete compliance ruleset
  deleteComplianceRuleset(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/ComplianceRuleset/${id}`, {
      headers: this.header,
    });
  }

  // Bulk delete compliance rulesets
  deleteComplianceRulesets(ids: number[]): Observable<any> {
    return this.http.request<any>('delete', `${this.baseUrl}/ComplianceRuleset`, {
      headers: this.header,
      body: [...ids],
    });
  }

  // Activate compliance ruleset, optionally keeping rules from the version it replaces
  activateComplianceRuleset(id: number, carryOverRules?: string[]): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/ComplianceRuleset/${id}/activate`,
      { carryOverRules: carryOverRules?.length ? carryOverRules : null },
      {
        headers: this.header,
      },
    );
  }

  /**
   * Region API methods - DEPRECATED
   * Use RegionService (src/app/services/region.service.ts) for all region-related operations
   */

  /**
   * Closes an impersonated maintenance session on the server.
   *
   * Needs the company token this session runs on, which is why it lives here and not in the
   * console: the console holds a platform token and would be refused. Best effort — the token
   * expires on its own, and a failed call must not strand the operator inside the company.
   */
  endImpersonation(): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/usermanagement/Auth/end-impersonation`,
      {},
      { headers: this.header },
    );
  }

  logout(email?: string): Observable<any> {
    // Clears every auth and company-scoped key, not just the two. The rest used to survive a
    // company switch on a shared browser.
    clearSession();

    if (email) {
      return this.http.post<any>(
        `${this.baseUrl}/usermanagement/Auth/logout?email=${email}`,
        {},
        {
          headers: this.header,
        },
      );
    } else {
      return new Observable((observer) => {
        observer.next({ success: true });
        observer.complete();
      });
    }
  }

  // Violations Methods
  getViolations(options?: {
    filters?: any;
    pagination?: { page: number; pageSize: number };
    sort?: { field: string; direction: 'asc' | 'desc' };
  }): Observable<any> {
    let params = new HttpParams();

    // Add filters
    if (options?.filters) {
      Object.keys(options.filters).forEach((key) => {
        const value = options.filters[key];
        if (value !== null && value !== undefined && value !== '') {
          if (key === 'startDate' || key === 'endDate') {
            params = params.set(key, new Date(value).toISOString());
          } else {
            params = params.set(key, value.toString());
          }
        }
      });
    }

    // Add pagination
    if (options?.pagination) {
      params = params.set('page', options.pagination.page.toString());
      params = params.set('pageSize', options.pagination.pageSize.toString());
    }

    // Add sorting
    if (options?.sort) {
      params = params.set('sortBy', options.sort.field);
      params = params.set('sortOrder', options.sort.direction);
    }

    return this.http.get<any>(`${this.baseUrl}/Violation`, {
      headers: this.header,
      params: params,
    });
  }

  getTeams(options?: {
    filters?: any;
    pagination?: { page: number; pageSize: number };
    sort?: { field: string; direction: 'asc' | 'desc' };
  }): Observable<any> {
    let params = new HttpParams();

    // Add filters
    if (options?.filters) {
      Object.keys(options.filters).forEach((key) => {
        const value = options.filters[key];
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value.toString());
        }
      });
    }

    // Add pagination
    if (options?.pagination) {
      params = params.set('page', options.pagination.page.toString());
      params = params.set('pageSize', options.pagination.pageSize.toString());
    }

    // Add sorting
    if (options?.sort) {
      params = params.set('sortBy', options.sort.field);
      params = params.set('sortOrder', options.sort.direction);
    }

    return this.http.get<any>(`${this.baseUrl}/usermanagement/Team`, {
      headers: this.header,
      params: params,
    });
  }

  // ========== NOTIFICATION METHODS ==========

  /**
   * Get paginated notifications for current user
   * @param page - Page number (default: 1)
   * @param pageSize - Items per page (default: 5)
   * @param isRead - Filter by read status (optional)
   */
  getNotifications(page: number = 1, pageSize: number = 5, isRead?: boolean): Observable<any> {
    let params = new HttpParams().set('page', page.toString()).set('pageSize', pageSize.toString());

    if (isRead !== undefined) {
      params = params.set('isRead', isRead.toString());
    }

    return this.http.get<any>(`${this.baseUrl}/Notification`, {
      headers: this.header,
      params,
    });
  }

  /**
   * Get recent unread notifications (for header dropdown)
   * @param limit - Number of notifications to fetch (default: 5)
   */
  getRecentNotifications(limit: number = 5): Observable<any> {
    const params = new HttpParams().set('limit', limit.toString());

    return this.http.get<any>(`${this.baseUrl}/Notification/recent`, {
      headers: this.header,
      params,
    });
  }

  /**
   * Get unread notification count (for badge)
   */
  getUnreadNotificationCount(): Observable<any> {
    // Silent 30s background poll (runs app-wide via the notification panel) — never show the loader.
    return this.http.get<any>(`${this.baseUrl}/Notification/unread-count`, {
      headers: this.header,
      context: new HttpContext().set(SKIP_LOADER, true),
    });
  }

  /**
   * Mark a specific notification as read
   * @param notificationId - ID of notification to mark as read
   */
  markNotificationAsRead(notificationId: number): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/Notification/${notificationId}/read`,
      {},
      { headers: this.header },
    );
  }

  /**
   * Mark all notifications as read for current user
   */
  markAllNotificationsAsRead(): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/Notification/read-all`,
      {},
      { headers: this.header },
    );
  }

  /**
   * Delete a notification
   * @param notificationId - ID of notification to delete
   */
  deleteNotification(notificationId: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/Notification/${notificationId}`, {
      headers: this.header,
    });
  }

  /**
   * Get all shifts with actual times for remediation calculations
   */
  getShiftsWithActualTimes(options?: {
    startDate?: string;
    endDate?: string;
    userId?: number;
    teamId?: number;
  }): Observable<any> {
    let params = new HttpParams();

    if (options?.startDate) params = params.set('startDate', options.startDate);
    if (options?.endDate) params = params.set('endDate', options.endDate);
    if (options?.userId) params = params.set('userId', options.userId.toString());
    if (options?.teamId) params = params.set('teamId', options.teamId.toString());

    return this.http.get<any>(`${this.baseUrl}/Shift/with-actual-times`, {
      headers: this.header,
      params: params,
    });
  }

  /**
   * Get all skipped breaks for remediation calculations
   */
  getSkippedBreaks(options?: {
    startDate?: string;
    endDate?: string;
    userId?: number;
    teamId?: number;
  }): Observable<any> {
    let params = new HttpParams();

    if (options?.startDate) params = params.set('startDate', options.startDate);
    if (options?.endDate) params = params.set('endDate', options.endDate);
    if (options?.userId) params = params.set('userId', options.userId.toString());
    if (options?.teamId) params = params.set('teamId', options.teamId.toString());

    return this.http.get<any>(`${this.baseUrl}/Break/skipped`, {
      headers: this.header,
      params: params,
    });
  }

  /**
   * Get remediation report data (combined endpoint)
   */
  getRemediationData(options?: {
    startDate?: string;
    endDate?: string;
    userId?: number;
    teamId?: number;
  }): Observable<any> {
    let params = new HttpParams();

    if (options?.startDate) params = params.set('startDate', options.startDate);
    if (options?.endDate) params = params.set('endDate', options.endDate);
    if (options?.userId) params = params.set('userId', options.userId.toString());
    if (options?.teamId) params = params.set('teamId', options.teamId.toString());

    return this.http.get<any>(`${this.baseUrl}/Report/remediation`, {
      headers: this.header,
      params: params,
    });
  }

  getMealComplianceReport(options?: {
    startDate?: string;
    endDate?: string;
    teamId?: number;
  }): Observable<any> {
    let params = new HttpParams();
    if (options?.startDate) params = params.set('startDate', options.startDate);
    if (options?.endDate) params = params.set('endDate', options.endDate);
    if (options?.teamId) params = params.set('teamId', options.teamId.toString());

    return this.http.get<any>(`${this.baseUrl}/Report/meal-break-compliance`, {
      headers: this.header,
      params: params,
    });
  }

  // ============================================
  // MOpInterval API Methods (Simulation Only)
  // ============================================

  createMOpInterval(payload: {
    employeeId: string;
    convertedDateTime: string;
    logoutReason?: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/MOpInterval`, payload, {
      headers: this.header,
    });
  }

  getMOpIntervals(options?: {
    employeeId?: string;
    startDate?: string;
    endDate?: string;
  }): Observable<any> {
    let params = new HttpParams();
    if (options?.employeeId) {
      params = params.set('employeeId', options.employeeId);
    }
    if (options?.startDate) {
      params = params.set('startDate', options.startDate);
    }
    if (options?.endDate) {
      params = params.set('endDate', options.endDate);
    }
    return this.http.get<any>(`${this.baseUrl}/MOpInterval`, {
      headers: this.header,
      params,
    });
  }

  getMOpIntervalById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/MOpInterval/${id}`, {
      headers: this.header,
    });
  }

  deleteMOpInterval(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/MOpInterval/${id}`, {
      headers: this.header,
    });
  }

  createBulkMOpIntervals(
    payload: Array<{ employeeId: string; convertedDateTime: string; logoutReason?: string }>,
  ): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/MOpInterval/bulk`, payload, {
      headers: this.header,
    });
  }
}
